import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Link, useLocation } from "react-router-dom";

export const EASE = [0.22, 1, 0.36, 1];

export const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

export const staggerContainer = (stagger = 0.08, delay = 0) => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
});

export const btnMotion = {
  whileHover: { scale: 1.04 },
  whileTap: { scale: 0.97 },
  transition: { duration: 0.15 },
};

export const Waveform = () => (
  <motion.svg
    className="cw-waveform"
    viewBox="0 0 600 90"
    preserveAspectRatio="none"
    aria-hidden="true"
    initial="hidden"
    animate="visible"
  >
    <motion.path
      className="cw-waveform__path"
      d="M0,45 L20,20 L38,68 L55,12 L72,74 L90,28 L108,58
         L130,42 C160,30 180,30 210,42
         C240,54 260,54 290,45
         C330,34 360,34 400,45
         C440,56 470,56 510,45
         C540,38 570,38 600,45"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={{ duration: 1.8, ease: "easeInOut" }}
    />
  </motion.svg>
);

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/platform", label: "Platform" },
  { to: "/for-employees", label: "For Employees" },
  { to: "/for-organizations", label: "For Organizations" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Scroll to top whenever the route changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <nav className="cw-nav" style={{position: 'sticky', top: 0, zIndex: 99999, background: 'var(--bg-color, #ffffff)', width: '100%'  }}>
      <div className="cw-nav__container" style={{width: '100%'}}>
        
        {/* Logo */}
        <motion.div
          className="cw-nav__logo-wrap"
          animate={{ 
            opacity: isOpen ? 0 : 1,
            pointerEvents: isOpen ? 'none' : 'auto'
          }}
          transition={{ duration: 0.2, ease: EASE }}
        >
          <Link to="/" className="cw-nav__logo">
            Cognito Wellness
          </Link>
        </motion.div>

        {/* Desktop Navigation */}
        <div className="cw-nav__desktop">
          <ul className="cw-nav__desktop-links">
            {LINKS.map((link) => (
              <li key={link.to}>
                <Link to={link.to} className="cw-nav__desktop-link">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <a 
            href="mailto:cs@cognitowellness.org"
            className="cw-btn cw-btn--primary" 
          >
            Book a Demo
          </a>
        </div>

        {/* Mobile Menu Toggle Button */}
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="cw-nav__mobile-toggle"
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
        >
          <motion.div
            animate={isOpen ? "open" : "closed"}
            style={{ width: '28px', height: '20px', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
          >
            <motion.span
              variants={{
                closed: { rotate: 0, y: 0 },
                open: { rotate: 45, y: 9 }
              }}
              transition={{ duration: 0.2, ease: EASE }}
              style={{ width: '100%', height: '2px', background: 'currentColor', display: 'block', borderRadius: '2px' }}
            />
            <motion.span
              variants={{
                closed: { opacity: 1 },
                open: { opacity: 0 }
              }}
              transition={{ duration: 0.1 }}
              style={{ width: '100%', height: '2px', background: 'currentColor', display: 'block', borderRadius: '2px' }}
            />
            <motion.span
              variants={{
                closed: { rotate: 0, y: 0 },
                open: { rotate: -45, y: -9 }
              }}
              transition={{ duration: 0.2, ease: EASE }}
              style={{ width: '100%', height: '2px', background: 'currentColor', display: 'block', borderRadius: '2px' }}
            />
          </motion.div>
        </button>
      </div>

      {/* Full-Screen Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: EASE }}
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              background: 'var(--bg-color, #ffffff)', 
              zIndex: 40,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '2rem'
            }}
          >
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '2rem', listStyle: 'none', margin: 0, padding: 0, textAlign: 'center' }}>
              {LINKS.map((link, index) => (
                <motion.li 
                  key={link.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + (index * 0.05), duration: 0.4, ease: EASE }}
                >
                  <Link 
                    to={link.to} 
                    style={{ fontSize: '1.75rem', fontWeight: 600, textDecoration: 'none', color: 'inherit', display: 'block' }}
                  >
                    {link.label}
                  </Link>
                </motion.li>
              ))}
              <motion.li 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.4, ease: EASE }}
                style={{ marginTop: '1.5rem' }}
              >
                <a 
                  href="mailto:cs@cognitowellness.org"
                  className="cw-btn cw-btn--primary"
                  style={{ display: 'inline-block', textAlign: 'center', padding: '1rem 2.5rem', textDecoration: 'none', fontSize: '1.1rem' }}
                >
                  Book a Demo
                </a>
              </motion.li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Responsive CSS */}
      <style>{`
        .cw-nav__container {
          display: flex;
          align-items: center;
          padding: 1rem 2rem;
          width: 100%;
          box-sizing: border-box;
        }

        .cw-nav__logo-wrap {
          flex-shrink: 0;
        }

        .cw-nav__logo {
          font-weight: 700;
          font-size: 1.25rem;
          text-decoration: none;
          color: inherit;
          white-space: nowrap;
        }

        .cw-nav__desktop {
          display: none;
          align-items: center;
          gap: 2rem;
          margin-left: auto;
        }

        .cw-nav__desktop-links {
          display: flex;
          gap: 1.75rem;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .cw-nav__desktop-link {
          text-decoration: none;
          color: inherit;
          font-size: 0.95rem;
          white-space: nowrap;
          transition: opacity 0.2s ease;
        }

        .cw-nav__desktop-link:hover {
          opacity: 0.7;
        }

        .cw-nav__mobile-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.5rem;
          color: inherit;
          z-index: 60;
          position: relative;
          margin-left: auto;
        }

        @media (min-width: 768px) {
          .cw-nav__desktop {
            display: flex;
          }
          .cw-nav__mobile-toggle {
            display: none;
          }
        }
      `}</style>
    </nav>
  );
};

export const FinalCTA = () => (
  <motion.section
    className="cw-section cw-section--alt"
    style={{ textAlign: 'center', marginTop: 'var(--space-xl)' }}
    variants={staggerContainer(0.08)}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.4 }}
  >
    <div className="cw-section__inner">
      <motion.h2 className="cw-section__title" variants={fadeUp} style={{ fontSize: '2.5rem', marginBottom: 'var(--space-sm)' }}>
        Support the person. Strengthen the organization.
      </motion.h2>
      <motion.p variants={fadeUp} style={{ marginBottom: 'var(--space-md)', opacity: 0.8 }}>
        Get a 3 month run to test out the platform and productivity boosts, if it fits, we can move to a long term contract.
      </motion.p>
      <motion.a
        href="mailto:cs@cognitowellness.org"
        className="cw-btn cw-btn--primary"
        variants={fadeUp}
        {...btnMotion}
      >
        Book a Demo
      </motion.a>
    </div>
  </motion.section>
);